<template>
  <div id="tree">
    <v-dialog v-model="displayParameters" hide-overlay width="400px">
      <v-card>
        <v-card-title>Layout parameters</v-card-title>
        <v-card-text>
          <v-switch v-model="showPhylogram" dense label="display cladogram instead of phylogram" />
          <v-switch v-model="alignLabels" dense label="Align labels" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="blue darken-1" text @click="displayParameters = false"> Close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card-gene-explore
      color="blue"
      title="Tree"
      :other-buttons="otherButtons"
      :height="height + 100"
      @hide="hideTree"
      @show-parameters="displayParameters = !displayParameters"
      @zoom-in="zoomIn"
      @zoom-out="zoomOut"
      @left="left"
      @right="right"
      @up="up"
      @down="down"
      @reset="reset"
      @deselectAll="deselectAll"
    >
      <v-card-subtitle>{{ nodeInfo.name }}</v-card-subtitle>
      <vue-phylogram
        id="phylo"
        ref="phylo"
        :input-tree="tree"
        :height="height"
        :width="width"
        :label-width="labelWidth"
        branch-length-key="branchLength"
        :branch-lengths="showPhylogram"
        :align-labels="alignLabels"
        :selected="nodesToSelect.join(',')"
        :display-leaves="false"
        :label-styles="labelStyles"
        :collapsed="collapsedIds"
        :glyphs="glyphs"
        @click-node="clickNodeFn"
        @select-nodes="selectNodes"
        @hover-node="hoverNodeFn"
        @hover-label="hoverNodeFn"
      />
    </v-card-gene-explore>
  </div>
</template>

<script>
import { VuePhylogram } from 'vue-phylogram';
import VCardGeneExplore from '@/components/generic/VCardGeneExplore.vue';

import { displayParameters } from '@/mixins/displayParameters.js';

export default {
  components: {
    VuePhylogram,
    VCardGeneExplore,
  },
  mixins: [displayParameters],
  props: {
    height: {
      type: Number,
      default: 800,
    },
    tree: {
      type: String,
      default: '',
    },
    nodesToSelect: {
      type: Array,
      default: () => {
        return [];
      },
    },
    width: {
      type: Number,
      default: 600,
    },
    highlightedLeaveIds: {
      type: Array,
      default: () => {
        return [];
      },
    },
    collapsed: {
      type: Array,
      default: () => {
        return [];
      },
    },
    glyphs: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
  data() {
    return {
      showPhylogram: true,
      alignLabels: true,
      nodeInfo: { name: 'hover a node to display its name' },
      otherButtons: [
        { icon: 'mdi-magnify-plus', event: 'zoom-in', tooltip: 'zoom in' },
        { icon: 'mdi-magnify-minus', event: 'zoom-out', tooltip: 'zoom out' },
        { icon: 'keyboard_arrow_left', event: 'left', tooltip: 'left' },
        { icon: 'keyboard_arrow_right', event: 'right', tooltip: 'right' },
        { icon: 'keyboard_arrow_up', event: 'up', tooltip: 'up' },
        { icon: 'keyboard_arrow_down', event: 'down', tooltip: 'down' },
        { icon: 'mdi-code-brackets', event: 'reset', tooltip: '[1:1]' },
        { icon: 'mdi-close-outline', event: 'deselectAll', tooltip: 'deselect all' },
      ],
      nbLeaves: 3,
    };
  },
  computed: {
    labelStyles() {
      let labelStyles = {};
      this.highlightedLeaveIds.forEach(id => {
        labelStyles[id] = {
          borderWidth: 1,
          borderColor: 'black',
        };
      });
      return labelStyles;
    },
    collapsedIds() {
      return this.collapsed.toString();
    },
    labelWidth() {
      let max = this.width / 2;
      let min = 200;
      let w = (1.5 * this.width) / Math.log(this.nbLeaves);

      if (w > max) w = max;
      if (w < min) w = min;
      return w;
    },
  },
  mounted() {
    this.nbLeaves = this.$refs.phylo.d3Leaves.length;
    this.$emit('ready', { nbLeaves: this.nbLeaves });
  },
  updated() {
    this.nbLeaves = this.$refs.phylo.d3Leaves.length;
    this.$emit('ready', { nbLeaves: this.nbLeaves });
  },
  methods: {
    clickNodeFn(e, node) {
      if ('branchset' in node.data) {
        this.$refs.phylo.deselectAll();
        this.$refs.phylo.selectNode(node);
      }
    },
    deselectAll() {
      this.$refs.phylo.deselectAll();
    },
    hoverNodeFn(e, node) {
      this.nodeInfo = { id: node.data.id, name: node.data.name };
    },
    selectNodes(nodes) {
      this.$emit('select-node', nodes);
    },
    zoomIn() {
      this.$refs.phylo.zoomIn();
    },
    zoomOut() {
      this.$refs.phylo.zoomOut();
    },
    left() {
      this.$refs.phylo.left();
    },
    right() {
      this.$refs.phylo.right();
    },
    up() {
      this.$refs.phylo.up();
    },
    down() {
      this.$refs.phylo.down();
    },
    reset() {
      this.$refs.phylo.reset();
    },
    hideTree() {
      this.$emit('hide-tree');
    },
  },
};
</script>

<style>
.svg-pan-zoom-control {
  left: 50px;
  bottom: 50px;
}
</style>
<style src="vue-phylogram/dist/vue-phylogram.css">
/* global styles */
</style>

<style scoped>
.v-label {
  font-size: 12px;
}
.title text {
  display: none;
}

.v-btn {
  margin: 5px;
}

.node-info {
  position: absolute;
  width: auto;
  height: auto;
  max-width: 50%;
  left: 10px;
  bottom: 50px;
  background-color: transparent;
  /* border: 1px solid purple;
  border-radius: 10px; */
  padding: 10px 3px 0px 3px;
}

.list-info {
  list-style: none inside;
}

.v-card__subtitle {
  padding: 0px;
}

.v-card {
  margin: 5px;
}

#phylo {
  margin: 10px;
}
</style>
